/**
 * What a closed-beta waitlist signup says about who someone is and what brought
 * them: the role they pick on the form, and the campaign tags (`utm_source`,
 * `utm_medium`, `utm_campaign`) on the link they first landed on.
 *
 * Pure and import-free, so the backend reads it by relative path
 * (`waitlistPolicy.ts` validates against `WAITLIST_ROLES`) and the admin app,
 * the marketing site and the unit tests share one copy. The role list and its
 * wire values are a contract between the form and the server: adding one is a
 * line here and an admin label in `waitlistPolicy.ts`, never a second list.
 */

/** Wire values. Stored verbatim on `Waitlists.role`. */
export const WAITLIST_ROLES = ['venue-booker', 'artist', 'host-promoter', 'invited', 'other'] as const;

export type WaitlistRole = (typeof WAITLIST_ROLES)[number];

/** The form's options, in the person's own words, in display order. */
export const WAITLIST_ROLE_OPTIONS: ReadonlyArray<{ value: WaitlistRole; label: string }> = [
    { value: 'venue-booker', label: 'I book shows at a venue' },
    { value: 'artist', label: 'I book my own shows (artist)' },
    { value: 'host-promoter', label: 'I host open mics or promote' },
    { value: 'invited', label: 'Someone sent me a Showliner link' },
    { value: 'other', label: 'Other' },
];

export function isWaitlistRole(value: unknown): value is WaitlistRole {
    return typeof value === 'string' && (WAITLIST_ROLES as readonly string[]).includes(value);
}

/** The three campaign tags we keep. Others (`utm_term`, `utm_content`) are ignored. */
export interface WaitlistUtm {
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
}

/** Query-string name for each field. */
const UTM_PARAMS: ReadonlyArray<[keyof WaitlistUtm, string]> = [
    ['utmSource', 'utm_source'],
    ['utmMedium', 'utm_medium'],
    ['utmCampaign', 'utm_campaign'],
];

/**
 * Longest tag kept. Ad platforms fill these from templates, and a tag is a
 * label, not a payload: anything longer is cut rather than refused, so a
 * mangled link still records the signup.
 */
export const UTM_MAX_LENGTH = 100;

export const EMPTY_UTM: WaitlistUtm = { utmSource: null, utmMedium: null, utmCampaign: null };

/** One tag as stored: trimmed, capped, and `null` for anything empty or not a string. */
export function normalizeUtmValue(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed.slice(0, UTM_MAX_LENGTH);
}

export function hasUtm(utm: WaitlistUtm | null | undefined): boolean {
    return Boolean(utm && (utm.utmSource || utm.utmMedium || utm.utmCampaign));
}

/** The tags on a query string (`location.search`, with or without the `?`). */
export function readUtm(search: string): WaitlistUtm {
    const params = new URLSearchParams(search);
    const utm = { ...EMPTY_UTM };
    for (const [field, param] of UTM_PARAMS) utm[field] = normalizeUtmValue(params.get(param));
    return utm;
}

/** The same fields from a stored or posted object, each normalized. */
export function utmFrom(raw: unknown): WaitlistUtm {
    const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
        utmSource: normalizeUtmValue(obj.utmSource),
        utmMedium: normalizeUtmValue(obj.utmMedium),
        utmCampaign: normalizeUtmValue(obj.utmCampaign),
    };
}

/** sessionStorage key. One per origin: the marketing site and admin each keep their own. */
export const UTM_STORAGE_KEY = 'showliner_utm';

/** The slice of `Storage` this needs, so tests can pass a plain object. */
export interface UtmStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

/**
 * Keep the tags of the link this visit started on. **First touch wins**: a
 * visitor who arrives from an ad and then clicks around keeps the ad's tags,
 * because an internal link carries none and the later tagged link (say, the
 * marketing site handing them to admin) names the same campaign anyway.
 * Storage that is missing or throws (a private window, blocked site data) is
 * skipped: losing a campaign tag must never break the page.
 */
export function rememberUtm(storage: UtmStorage | null | undefined, search: string): void {
    if (!storage) return;
    const incoming = readUtm(search);
    if (!hasUtm(incoming)) return;
    try {
        if (hasUtm(recalledUtm(storage))) return;
        storage.setItem(UTM_STORAGE_KEY, JSON.stringify(incoming));
    } catch {
        // Storage unavailable. The form still works without the tags.
    }
}

/** The tags remembered for this visit, or empty ones. */
export function recalledUtm(storage: UtmStorage | null | undefined): WaitlistUtm {
    if (!storage) return { ...EMPTY_UTM };
    try {
        const raw = storage.getItem(UTM_STORAGE_KEY);
        return raw ? utmFrom(JSON.parse(raw)) : { ...EMPTY_UTM };
    } catch {
        return { ...EMPTY_UTM };
    }
}

/**
 * `url` with the tags added as `utm_*` params, for a link that crosses to
 * another origin (sessionStorage does not). A tag already on the URL is kept.
 */
export function withUtm(url: string, utm: WaitlistUtm): string {
    if (!hasUtm(utm)) return url;
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return url;
    }
    for (const [field, param] of UTM_PARAMS) {
        const value = utm[field];
        if (value && !parsed.searchParams.has(param)) parsed.searchParams.set(param, value);
    }
    return parsed.toString();
}

/** `window.sessionStorage`, or `null` where reading it throws. */
export function sessionStorageOrNull(): UtmStorage | null {
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null;
    } catch {
        return null;
    }
}

/**
 * The tags to send with a signup: the ones remembered for this visit, else the
 * ones on the current URL (storage can be unavailable, and then the landing
 * URL is still right there when the form is on it).
 */
export function currentVisitUtm(storage: UtmStorage | null | undefined, search: string): WaitlistUtm {
    const remembered = recalledUtm(storage);
    return hasUtm(remembered) ? remembered : readUtm(search);
}
